import React, { useMemo, useState } from 'react';
import { modalStyles as styles } from './modalStyles';
import { colors } from '../theme';

const ACCESSORY_LABELS = {
  ac_power_cable: 'AC power cable',
  hdmi_cable: 'HDMI cable',
  dp_cable: 'DP cable',
  touch_cable: 'Touch cable (USB A–USB B)'
};

const canonicalAccessoryCode = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/qty$/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
};

const formatAccessoryLabel = code => {
  const canonical = canonicalAccessoryCode(code);
  if (canonical && ACCESSORY_LABELS[canonical]) return ACCESSORY_LABELS[canonical];
  const src = typeof code === 'string' && code.trim() ? code.trim() : canonical;
  if (!src) return 'Accessory';
  return src.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
};

const formatAccMap = map => {
  const entries = Object.entries(map).filter(([, qty]) => qty > 0);
  if (!entries.length) return 'None declared';
  return entries
    .map(([code, qty]) => qty > 1 ? `${formatAccessoryLabel(code)} x${qty}` : formatAccessoryLabel(code))
    .join(', ');
};

export default function CustomerRmaModalRepairing({
  rma, onClose,
  onCustomerDecision, customerDecisionState,
  onChallenge, challengeSubmittingId,
  onDeclinePath, declinePathState
}) {
  const receipt = rma?.receipt && typeof rma.receipt === 'object' ? rma.receipt : null;
  const receiptFound = receipt?.found || null;

  // Build expected accessories map from customer's original declaration
  const expectedAccMap = useMemo(() => {
    const acc = rma?.expectedReturn?.accessories;
    if (!acc || typeof acc !== 'object') return {};
    const map = {};
    Object.entries(acc).forEach(([key, qty]) => {
      const canonical = canonicalAccessoryCode(key);
      if (canonical && (Number(qty) || 0) > 0) map[canonical] = Number(qty);
    });
    return map;
  }, [rma?.expectedReturn]);

  // Build found accessories map from receipt
  const foundAccMap = useMemo(() => {
    const acc = Array.isArray(receiptFound?.accessories) ? receiptFound.accessories : [];
    const map = {};
    acc.forEach(entry => {
      if (!entry || entry.included === false) return;
      const canonical = canonicalAccessoryCode(entry.code || '');
      const qty = Math.max(0, Number(entry.qty) || 0);
      if (canonical && qty > 0) map[canonical] = (map[canonical] || 0) + qty;
    });
    return map;
  }, [receiptFound]);

  // Compute accessory differences
  const accessoryDiffs = useMemo(() => {
    const allCodes = new Set([...Object.keys(expectedAccMap), ...Object.keys(foundAccMap)]);
    const diffs = [];
    allCodes.forEach(code => {
      const exp = expectedAccMap[code] || 0;
      const found = foundAccMap[code] || 0;
      if (exp !== found) diffs.push({ code, expected: exp, found });
    });
    return diffs;
  }, [expectedAccMap, foundAccMap]);

  // Non-accessory mismatches from stored receipt
  const nonAccessoryMismatchFields = useMemo(() => {
    const fields = Array.isArray(receipt?.mismatch?.fields) ? receipt.mismatch.fields : [];
    return fields.filter(f => !f.startsWith('accessories:'));
  }, [receipt]);

  const hasAccessoryMismatch = accessoryDiffs.length > 0;
  const hasNonAccessoryMismatch = nonAccessoryMismatchFields.length > 0;
  const hasMismatch = hasAccessoryMismatch || hasNonAccessoryMismatch;
  const intakeConfirmed = Boolean(receipt?.confirmedAt);

  const warrantyDecision = rma?.repair?.warranty?.decision || null;
  const warrantyAccepted = warrantyDecision === 'accepted';
  const warrantyDenied = warrantyDecision === 'denied';

  const repairTasks = Array.isArray(rma?.repair?.tasks) ? rma.repair.tasks.filter(Boolean) : [];

  // Customer decision state
  const repairData = rma?.repair && typeof rma.repair === 'object' ? rma.repair : {};
  const paymentData = repairData.payment && typeof repairData.payment === 'object' ? repairData.payment : {};
  const paymentRequired = paymentData.required === true;
  const paymentReceived = paymentData.status === 'received';
  const repairPhase = repairData.phase === 'execution' ? 'execution' : 'inspection';
  const customerApproval = repairData.customerApproval && typeof repairData.customerApproval === 'object'
    ? repairData.customerApproval : {};
  const approvalRequired = customerApproval.required === true;
  const approvalStatus = customerApproval.status || 'pending';
  const customerDeclined = approvalStatus === 'declined';
  const postDecline = rma?.postDecline && typeof rma.postDecline === 'object' ? rma.postDecline : {};
  const postDeclineChoice = postDecline.choice || null;
  const rawChallenges = Array.isArray(repairData.challenges) ? repairData.challenges.filter(Boolean) : [];
  const sortedChallenges = [...rawChallenges].sort((a, b) => (Number(a?.round) || 0) - (Number(b?.round) || 0));
  const latestChallenge = sortedChallenges.length > 0 ? sortedChallenges[sortedChallenges.length - 1] : null;
  const latestChallengeRound = Number(latestChallenge?.round) || Number(repairData.challengeRound) || 0;
  const latestChallengeOutcome = latestChallenge?.review?.outcome || '';
  const latestChallengeSubmittedAt = latestChallenge?.submittedAt
    ? new Date(latestChallenge.submittedAt).toLocaleString('no-NO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;
  const challengePending = Boolean(latestChallenge && !latestChallengeOutcome);
  const challengeDeclined = latestChallengeOutcome === 'declined';
  const challengeRoundText = latestChallengeRound ? ` (Round #${latestChallengeRound})` : '';

  const canShowDecisionActions = approvalRequired &&
    approvalStatus === 'pending' &&
    typeof onCustomerDecision === 'function';
  const canShowChallengeAction = approvalRequired &&
    warrantyDecision === 'denied' &&
    repairPhase === 'inspection' &&
    !challengePending &&
    !postDeclineChoice &&
    typeof onChallenge === 'function';
  const declineRoutingOpen = customerDeclined && !postDeclineChoice && typeof onDeclinePath === 'function';

  const decisionState = customerDecisionState || {};
  const decisionLoading = Boolean(decisionState.loading);
  const decisionError = decisionState.error || '';
  const declineState = declinePathState || {};
  const declineLoading = Boolean(declineState.loading);
  const declineError = declineState.error || '';

  const [shippingAckChecked, setShippingAckChecked] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null); // 'accept' | 'decline' | 'challenge'
  const [hoveredCard, setHoveredCard] = useState(null);
  const [challengeMessage, setChallengeMessage] = useState('');
  const [challengeFiles, setChallengeFiles] = useState([]);
  const [challengeSubmitting, setChallengeSubmitting] = useState(false);
  const [challengeError, setChallengeError] = useState('');

  const quoteAmount = repairData.quote?.amount;
  const formattedQuote = quoteAmount != null
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(quoteAmount))
    : null;
  const repairDays = repairData.timing?.repairDays > 0 ? repairData.timing.repairDays : null;
  const warrantyNote = repairData.warranty?.note || '';

  const handleAcceptRepair = () => {
    if (!decisionLoading && typeof onCustomerDecision === 'function') onCustomerDecision(rma, 'accepted');
  };

  const handleReturnAsIs = () => {
    if (!declineLoading && shippingAckChecked && typeof onDeclinePath === 'function') {
      onDeclinePath(rma, 'return_as_is');
    }
  };
  const handleCloseCase = () => {
    if (!declineLoading && typeof onDeclinePath === 'function') {
      onDeclinePath(rma, 'retain_and_close');
    }
  };

  const handleChallengeFileChange = e => {
    const files = Array.from(e.target.files || []).filter(f =>
      ['image/jpeg', 'image/png', 'application/pdf'].includes(f.type)
    ).slice(0, 5);
    setChallengeFiles(files);
  };

  const handleSubmitChallenge = async () => {
    if (challengeMessage.trim().length < 10) { setChallengeError('Please write at least 10 characters describing your challenge.'); return; }
    setChallengeSubmitting(true);
    setChallengeError('');
    try {
      const formData = new FormData();
      formData.append('message', challengeMessage.trim());
      challengeFiles.forEach(f => formData.append('attachments', f));
      const headers = {};
      if (typeof onChallenge === 'function') {
        // Use existing challenge modal flow — pass message via state hack not viable here,
        // so call the API directly
      }
      const res = await fetch(`${process.env.REACT_APP_API_URL}/rma/${rma._id}/challenge`, {
        method: 'POST',
        headers,
        body: formData
      });
      if (!res.ok) throw new Error('Failed to submit challenge.');
      const data = await res.json();
      setExpandedCard(null);
      setChallengeMessage('');
      setChallengeFiles([]);
      if (typeof onChallenge === 'function') onChallenge(rma, { submitted: true, updatedRma: data.rma });
    } catch (err) {
      setChallengeError(err.message || 'Failed to submit.');
    } finally {
      setChallengeSubmitting(false);
    }
  };

  // Banner for current approval state
  let approvalBanner = null;
  if (postDeclineChoice === 'retain_and_close' || rma?.ui?.decommissioned) {
    approvalBanner = { color: colors.danger, bg: 'rgba(236,33,36,0.1)', border: 'rgba(236,33,36,0.2)', text: 'Repair declined — case closed and device will be decommissioned.' };
  } else if (postDeclineChoice === 'return_as_is') {
    approvalBanner = { color: colors.textStrong, bg: 'rgba(236,33,36,0.07)', border: 'rgba(236,33,36,0.15)', text: 'Repair declined — device will be returned as-is once shipping is arranged.' };
  } else if (customerDeclined && !postDeclineChoice) {
    approvalBanner = { color: colors.textStrong, bg: 'rgba(236,33,36,0.07)', border: 'rgba(236,33,36,0.15)', text: 'Repair declined — choose how to proceed below.' };
  } else if (approvalStatus === 'accepted' && latestChallengeOutcome !== 'accepted') {
    approvalBanner = { color: colors.accent, bg: 'rgba(70,187,137,0.1)', border: 'rgba(70,187,137,0.2)', text: 'Accepted — proceeding with repair (Stage 2/2).' };
  } else if (challengePending) {
    approvalBanner = { color: colors.textStrong, bg: 'rgba(255,193,7,0.08)', border: 'rgba(255,193,7,0.2)',
      text: `Challenge submitted${challengeRoundText}${latestChallengeSubmittedAt ? ` — ${latestChallengeSubmittedAt}` : ''}. Our team will review your information.` };
  } else if (challengeDeclined) {
    approvalBanner = { color: colors.textStrong, bg: 'rgba(236,33,36,0.07)', border: 'rgba(236,33,36,0.15)',
      text: `Your previous challenge${challengeRoundText} was declined. You may accept the quoted repair, decline, or submit another challenge.` };
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Service In Progress</h2>
        <p style={{ color: colors.textMuted }}>
          The unit has begun repair. Please wait for North Invent to contact you if we have any
          questions regarding the process.
        </p>

        {intakeConfirmed && (
          <div
            style={{
              marginTop: '1rem',
              border: `1px solid ${hasMismatch ? 'rgba(255,193,7,0.3)' : 'rgba(70,187,137,0.25)'}`,
              borderRadius: '12px',
              padding: '0.9rem 1rem',
              backgroundColor: hasMismatch ? 'rgba(255,193,7,0.07)' : 'rgba(70,187,137,0.07)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 700,
                fontSize: '0.95rem'
              }}
            >
              <span style={{ color: colors.textStrong }}>Intake check</span>
              <span style={{ color: hasMismatch ? '#FFC107' : colors.accent }}>
                {hasMismatch ? 'Mismatch noted' : 'Match'}
              </span>
            </div>

            {!hasMismatch && (
              <p style={{ margin: 0, fontSize: '0.85rem', color: colors.textMuted }}>
                Everything received matched what you declared in your RMA submission.
              </p>
            )}

            {hasAccessoryMismatch && (
              <div style={{ fontSize: '0.85rem', color: colors.textBody }}>
                <div style={{ fontWeight: 600, marginBottom: '0.3rem', color: colors.textStrong }}>
                  Accessories
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <div>
                    <span style={{ color: colors.textMuted }}>Declared: </span>
                    {formatAccMap(expectedAccMap)}
                  </div>
                  <div>
                    <span style={{ color: colors.textMuted }}>Received: </span>
                    {Object.keys(foundAccMap).length > 0 ? formatAccMap(foundAccMap) : 'None received'}
                  </div>
                  {accessoryDiffs.map(({ code, expected, found }) => (
                    <div key={code} style={{ color: '#FFC107', fontSize: '0.8rem' }}>
                      {formatAccessoryLabel(code)}: declared {expected > 0 ? `x${expected}` : 'none'},{' '}
                      received {found > 0 ? `x${found}` : 'none'}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasNonAccessoryMismatch && (
              <div style={{ fontSize: '0.85rem', color: colors.textBody }}>
                <span style={{ color: colors.textMuted }}>Other differences: </span>
                {nonAccessoryMismatchFields
                  .map(f => {
                    if (f === 'mountingOption') return 'mounting option';
                    if (f === 'hmiIncluded') return 'HMI';
                    if (f === 'mainDeviceSerial') return 'serial number';
                    return f.replace(/[_:.]+/g, ' ');
                  })
                  .join(', ')}
              </div>
            )}

            {hasMismatch && (
              <p style={{ margin: 0, fontSize: '0.8rem', color: colors.textMuted }}>
                Our team has noted this and will handle it during the repair process.
              </p>
            )}
          </div>
        )}

        {(warrantyAccepted || warrantyDenied) && (
          <div
            style={{
              marginTop: '1rem',
              border: `1px solid ${warrantyAccepted ? 'rgba(70,187,137,0.25)' : 'rgba(248,113,113,0.3)'}`,
              borderRadius: '12px',
              padding: '0.9rem 1rem',
              backgroundColor: warrantyAccepted ? 'rgba(70,187,137,0.07)' : 'rgba(248,113,113,0.07)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 700,
                fontSize: '0.95rem'
              }}
            >
              <span style={{ color: colors.textStrong }}>Warranty decision</span>
              <span style={{ color: warrantyAccepted ? colors.accent : '#f87171' }}>
                {warrantyAccepted ? 'Accepted' : 'Denied'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: colors.textMuted }}>
              {warrantyAccepted
                ? 'Your repair is covered under warranty. No charges will apply.'
                : 'Your repair does not qualify for warranty coverage. Our team will be in touch.'}
            </p>
          </div>
        )}

        {paymentRequired && (
          <div style={{
            marginTop: '1rem',
            borderRadius: '12px',
            padding: '0.9rem 1rem',
            border: paymentReceived ? '1px solid rgba(70,187,137,0.25)' : '1px solid rgba(251,146,60,0.4)',
            backgroundColor: paymentReceived ? 'rgba(70,187,137,0.07)' : 'rgba(251,146,60,0.07)',
            display: 'flex', flexDirection: 'column', gap: '0.4rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: '0.95rem' }}>
              <span style={{ color: colors.textStrong }}>Payment</span>
              <span style={{ color: paymentReceived ? colors.accent : '#fb923c' }}>
                {paymentReceived ? 'Confirmed' : 'Pending'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: colors.textMuted }}>
              {paymentReceived
                ? 'Payment has been received. North Invent will now proceed with the repair.'
                : 'Payment is required before the repair can begin. North Invent will contact you with payment details.'}
            </p>
          </div>
        )}

        {repairTasks.length > 0 && (
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>
              Service Tasks
            </div>
            {repairTasks.map((task, index) => {
              const label = task?.label || `Task ${index + 1}`;
              const note = task?.note ? String(task.note) : '';
              const done = Boolean(task?.done);
              return (
                <div key={index} style={{
                  display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                  padding: '0.5rem 0.65rem', borderRadius: '8px',
                  backgroundColor: done ? 'rgba(70,187,137,0.07)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${done ? 'rgba(70,187,137,0.2)' : 'rgba(255,255,255,0.07)'}`
                }}>
                  <span style={{ color: done ? colors.accent : colors.textDim, fontWeight: 700, fontSize: '0.9rem', marginTop: '0.05rem' }}>
                    {done ? '✓' : '○'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: done ? colors.textStrong : colors.textBody }}>{label}</span>
                    {note && <span style={{ display: 'block', fontSize: '0.82rem', color: colors.textDim, marginTop: '0.1rem' }}>{note}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Approval banner */}
        {approvalRequired && approvalBanner && (
          <div style={{
            marginTop: '1rem',
            borderRadius: '10px',
            padding: '0.65rem 0.85rem',
            fontSize: '0.88rem',
            backgroundColor: approvalBanner.bg,
            border: `1px solid ${approvalBanner.border}`,
            color: approvalBanner.color
          }}>
            {approvalBanner.text}
          </div>
        )}

        {/* ── Decision cards ── */}
        {(canShowDecisionActions || canShowChallengeAction) && (
          <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>
              Your decision required
            </div>

            {/* ── ACCEPT ── */}
            {canShowDecisionActions && (
              <div
                onMouseEnter={() => setHoveredCard('accept')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  borderRadius: '12px',
                  border: `1.5px solid ${expandedCard === 'accept' ? 'rgba(70,187,137,0.7)' : hoveredCard === 'accept' ? 'rgba(70,187,137,0.55)' : 'rgba(70,187,137,0.3)'}`,
                  backgroundColor: expandedCard === 'accept' ? 'rgba(70,187,137,0.12)' : hoveredCard === 'accept' ? 'rgba(70,187,137,0.1)' : 'rgba(70,187,137,0.06)',
                  overflow: 'hidden', transition: 'all 0.15s',
                  cursor: 'pointer'
                }}>
                <button
                  type="button"
                  onClick={() => setExpandedCard(expandedCard === 'accept' ? null : 'accept')}
                  style={{
                    width: '100%', padding: '0.85rem 1.1rem', background: 'none', border: 'none',
                    display: 'flex', alignItems: 'center', gap: '0.65rem',
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '1.1rem', color: colors.accent }}>✓</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.accent, display: 'block' }}>Accept repair</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(70,187,137,0.7)' }}>
                      {formattedQuote && repairDays
                        ? `${formattedQuote} · Est. ${repairDays} days — click to confirm`
                        : formattedQuote
                        ? `${formattedQuote} — click to confirm`
                        : 'Click to confirm and proceed with repair'}
                    </span>
                  </span>
                  <span style={{ color: 'rgba(70,187,137,0.5)', fontSize: '0.85rem' }}>{expandedCard === 'accept' ? '▲' : '▼'}</span>
                </button>
                {expandedCard === 'accept' && (
                  <div style={{ padding: '0 1.1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {formattedQuote && (
                        <div style={{ padding: '0.4rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.3)', fontSize: '0.85rem', color: '#fb923c', fontWeight: 700 }}>
                          Cost: {formattedQuote}
                        </div>
                      )}
                      {repairDays && (
                        <div style={{ padding: '0.4rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(99,179,237,0.1)', border: '1px solid rgba(99,179,237,0.25)', fontSize: '0.85rem', color: '#63b3ed', fontWeight: 700 }}>
                          Est. {repairDays} days
                        </div>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.83rem', color: colors.textMuted }}>
                      By accepting you authorise North Invent to proceed with the quoted repair. Payment is due upon completion.
                    </p>
                    <button
                      type="button"
                      onClick={handleAcceptRepair}
                      disabled={decisionLoading}
                      style={{
                        alignSelf: 'flex-start', padding: '0.55rem 1.3rem',
                        borderRadius: '8px', border: 'none',
                        backgroundColor: decisionLoading ? 'rgba(70,187,137,0.4)' : colors.accent,
                        color: '#11201c', fontWeight: 700, fontSize: '0.9rem',
                        cursor: decisionLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {decisionLoading ? 'Submitting…' : 'Confirm — accept repair'}
                    </button>
                    {decisionError && <div style={{ color: colors.danger, fontSize: '0.82rem' }}>{decisionError}</div>}
                  </div>
                )}
              </div>
            )}

            {/* ── DECLINE ── */}
            {canShowDecisionActions && (
              <div
                onMouseEnter={() => setHoveredCard('decline')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  borderRadius: '12px',
                  border: `1.5px solid ${expandedCard === 'decline' ? 'rgba(248,113,113,0.6)' : hoveredCard === 'decline' ? 'rgba(248,113,113,0.5)' : 'rgba(248,113,113,0.25)'}`,
                  backgroundColor: expandedCard === 'decline' ? 'rgba(248,113,113,0.09)' : hoveredCard === 'decline' ? 'rgba(248,113,113,0.08)' : 'rgba(248,113,113,0.04)',
                  overflow: 'hidden', transition: 'all 0.15s',
                  cursor: 'pointer'
                }}>
                <button
                  type="button"
                  onClick={() => setExpandedCard(expandedCard === 'decline' ? null : 'decline')}
                  style={{
                    width: '100%', padding: '0.85rem 1.1rem', background: 'none', border: 'none',
                    display: 'flex', alignItems: 'center', gap: '0.65rem',
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '1.1rem', color: '#f87171' }}>✕</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#f87171', display: 'block' }}>Decline repair</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(248,113,113,0.6)' }}>
                      Refuse the repair — choose to return the device or close the case
                    </span>
                  </span>
                  <span style={{ color: 'rgba(248,113,113,0.4)', fontSize: '0.85rem' }}>{expandedCard === 'decline' ? '▲' : '▼'}</span>
                </button>
                {expandedCard === 'decline' && (
                  <div style={{ padding: '0 1.1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    {/* Return as-is */}
                    <div style={{
                      borderRadius: '10px', border: '1px solid rgba(70,187,137,0.25)',
                      backgroundColor: 'rgba(70,187,137,0.05)', padding: '0.75rem',
                      display: 'flex', flexDirection: 'column', gap: '0.5rem'
                    }}>
                      <div style={{ fontWeight: 700, color: colors.textStrong, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span>↩</span> Return device as-is
                      </div>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: colors.textMuted }}>
                        Your device will be shipped back unrepaired. <span style={{ color: '#fbbf24', fontWeight: 600 }}>You are responsible for the return shipping cost.</span>
                      </p>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: colors.textMuted, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={shippingAckChecked}
                          onChange={e => setShippingAckChecked(e.target.checked)}
                          disabled={declineLoading}
                        />
                        I understand I must cover all return shipping costs.
                      </label>
                      <button
                        type="button"
                        onClick={handleReturnAsIs}
                        disabled={declineLoading || !shippingAckChecked}
                        style={{
                          alignSelf: 'flex-start', padding: '0.5rem 1.1rem',
                          borderRadius: '8px', border: 'none',
                          backgroundColor: declineLoading || !shippingAckChecked ? 'rgba(147,149,151,0.2)' : colors.accent,
                          color: declineLoading || !shippingAckChecked ? colors.textMuted : '#11201c',
                          fontWeight: 700, fontSize: '0.88rem',
                          cursor: declineLoading || !shippingAckChecked ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {declineLoading ? 'Submitting…' : 'Return device'}
                      </button>
                    </div>
                    {/* Scrap / close */}
                    <div style={{
                      borderRadius: '10px', border: '1px solid rgba(248,113,113,0.2)',
                      backgroundColor: 'rgba(248,113,113,0.05)', padding: '0.75rem',
                      display: 'flex', flexDirection: 'column', gap: '0.5rem'
                    }}>
                      <div style={{ fontWeight: 700, color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span>✕</span> Close case — do not return
                      </div>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: colors.textMuted }}>
                        The case will be permanently closed and the device handled internally. <span style={{ color: '#f87171', fontWeight: 600 }}>This cannot be undone.</span>
                      </p>
                      <button
                        type="button"
                        onClick={handleCloseCase}
                        disabled={declineLoading}
                        style={{
                          alignSelf: 'flex-start', padding: '0.5rem 1.1rem',
                          borderRadius: '8px', border: 'none',
                          backgroundColor: declineLoading ? 'rgba(147,149,151,0.2)' : 'rgba(248,113,113,0.8)',
                          color: declineLoading ? colors.textMuted : '#fff',
                          fontWeight: 700, fontSize: '0.88rem',
                          cursor: declineLoading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {declineLoading ? 'Submitting…' : 'Close case'}
                      </button>
                    </div>
                    {declineError && <div style={{ color: colors.danger, fontSize: '0.82rem' }}>{declineError}</div>}
                  </div>
                )}
              </div>
            )}

            {/* ── CHALLENGE ── */}
            {canShowChallengeAction && (
              <div
                onMouseEnter={() => setHoveredCard('challenge')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  borderRadius: '12px',
                  border: `1.5px solid ${expandedCard === 'challenge' ? 'rgba(251,191,36,0.6)' : hoveredCard === 'challenge' ? 'rgba(251,191,36,0.5)' : 'rgba(251,191,36,0.25)'}`,
                  backgroundColor: expandedCard === 'challenge' ? 'rgba(251,191,36,0.08)' : hoveredCard === 'challenge' ? 'rgba(251,191,36,0.07)' : 'rgba(251,191,36,0.03)',
                  overflow: 'hidden', transition: 'all 0.15s',
                  cursor: 'pointer'
                }}>
                <button
                  type="button"
                  onClick={() => setExpandedCard(expandedCard === 'challenge' ? null : 'challenge')}
                  style={{
                    width: '100%', padding: '0.85rem 1.1rem', background: 'none', border: 'none',
                    display: 'flex', alignItems: 'center', gap: '0.65rem',
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '1.1rem', color: '#fbbf24' }}>⚑</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fbbf24', display: 'block' }}>Challenge claim</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(251,191,36,0.6)' }}>
                      Dispute the warranty denial — submit evidence for our team to review
                    </span>
                  </span>
                  <span style={{ color: 'rgba(251,191,36,0.4)', fontSize: '0.85rem' }}>{expandedCard === 'challenge' ? '▲' : '▼'}</span>
                </button>
                {expandedCard === 'challenge' && (
                  <div style={{ padding: '0 1.1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    {warrantyNote && (
                      <div style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.83rem', color: colors.textMuted }}>
                        <span style={{ fontWeight: 600, color: colors.textBody }}>NI reason: </span>{warrantyNote}
                      </div>
                    )}
                    <p style={{ margin: 0, fontSize: '0.83rem', color: colors.textMuted }}>
                      Explain why you believe the issue is covered under warranty. Attach photos or documents as evidence. Our team will review and respond.
                    </p>
                    <textarea
                      value={challengeMessage}
                      onChange={e => setChallengeMessage(e.target.value)}
                      placeholder="Describe your challenge — what evidence do you have that this should be covered?"
                      style={{
                        width: '100%', minHeight: '90px', borderRadius: '8px', boxSizing: 'border-box',
                        border: '1px solid rgba(251,191,36,0.3)', backgroundColor: 'rgba(255,255,255,0.04)',
                        color: colors.textBody, padding: '0.6rem 0.75rem', fontSize: '0.88rem',
                        resize: 'vertical', outline: 'none'
                      }}
                    />
                    <div>
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.45rem 0.9rem', borderRadius: '8px', cursor: 'pointer',
                        border: '1px solid rgba(251,191,36,0.3)', backgroundColor: 'rgba(251,191,36,0.07)',
                        color: '#fbbf24', fontSize: '0.85rem', fontWeight: 600
                      }}>
                        📎 Attach files
                        <input
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          multiple
                          onChange={handleChallengeFileChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {challengeFiles.length > 0 && (
                        <div style={{ marginTop: '0.4rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                          {challengeFiles.map((f, i) => (
                            <span key={i} style={{ fontSize: '0.78rem', color: colors.textMuted, padding: '0.2rem 0.5rem', borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                              {f.name}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: '0.75rem', color: colors.textDim, marginTop: '0.3rem' }}>
                        JPG, PNG or PDF · max 5 files · 10 MB each
                      </div>
                    </div>
                    {challengeError && <div style={{ color: colors.danger, fontSize: '0.82rem' }}>{challengeError}</div>}
                    <button
                      type="button"
                      onClick={handleSubmitChallenge}
                      disabled={challengeSubmitting || challengeMessage.trim().length < 10}
                      style={{
                        alignSelf: 'flex-start', padding: '0.55rem 1.3rem',
                        borderRadius: '8px', border: 'none',
                        backgroundColor: challengeSubmitting || !challengeMessage.trim() ? 'rgba(147,149,151,0.2)' : '#fbbf24',
                        color: challengeSubmitting || !challengeMessage.trim() ? colors.textMuted : '#1a1200',
                        fontWeight: 700, fontSize: '0.9rem',
                        cursor: challengeSubmitting || !challengeMessage.trim() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {challengeSubmitting ? 'Submitting…' : 'Submit challenge'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {decisionError && !expandedCard && <div style={{ color: colors.danger, fontSize: '0.82rem' }}>{decisionError}</div>}
          </div>
        )}

        {/* Decline routing — shown when already declined but path not chosen */}
        {declineRoutingOpen && (
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>How would you like to proceed?</div>
            <div style={{ borderRadius: '10px', border: '1px solid rgba(70,187,137,0.25)', backgroundColor: 'rgba(70,187,137,0.05)', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ fontWeight: 700, color: colors.textStrong }}>↩ Return device as-is</div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: colors.textMuted }}>Your device will be shipped back unrepaired. <span style={{ color: '#fbbf24', fontWeight: 600 }}>You cover return shipping costs.</span></p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: colors.textMuted, cursor: 'pointer' }}>
                <input type="checkbox" checked={shippingAckChecked} onChange={e => setShippingAckChecked(e.target.checked)} disabled={declineLoading} />
                I understand I must cover all return shipping costs.
              </label>
              <button type="button" onClick={handleReturnAsIs} disabled={declineLoading || !shippingAckChecked}
                style={{ alignSelf: 'flex-start', padding: '0.5rem 1.1rem', borderRadius: '8px', border: 'none', backgroundColor: declineLoading || !shippingAckChecked ? 'rgba(147,149,151,0.2)' : colors.accent, color: declineLoading || !shippingAckChecked ? colors.textMuted : '#11201c', fontWeight: 700, fontSize: '0.88rem', cursor: declineLoading || !shippingAckChecked ? 'not-allowed' : 'pointer' }}>
                {declineLoading ? 'Submitting…' : 'Return device'}
              </button>
            </div>
            <div style={{ borderRadius: '10px', border: '1px solid rgba(248,113,113,0.2)', backgroundColor: 'rgba(248,113,113,0.05)', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ fontWeight: 700, color: '#f87171' }}>✕ Close case — do not return</div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: colors.textMuted }}>The case will be permanently closed. <span style={{ color: '#f87171', fontWeight: 600 }}>This cannot be undone.</span></p>
              <button type="button" onClick={handleCloseCase} disabled={declineLoading}
                style={{ alignSelf: 'flex-start', padding: '0.5rem 1.1rem', borderRadius: '8px', border: 'none', backgroundColor: declineLoading ? 'rgba(147,149,151,0.2)' : 'rgba(248,113,113,0.8)', color: declineLoading ? colors.textMuted : '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: declineLoading ? 'not-allowed' : 'pointer' }}>
                {declineLoading ? 'Submitting…' : 'Close case'}
              </button>
            </div>
            {declineError && <div style={{ color: colors.danger, fontSize: '0.82rem' }}>{declineError}</div>}
          </div>
        )}

        <div style={{ marginTop: '1.25rem' }}>
          <button onClick={onClose} style={styles.button}>Close</button>
        </div>
      </div>
    </div>
  );
}
